import { Button } from '@components/Button';
import { EInput, Form, Input } from '@components/Input';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ui/card';
import { Loader2 } from 'lucide-react';
import z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { authQueries } from '@queries/index';
import { authService } from '@services/api';

export const Route = createFileRoute('/(private)/selecionar-estabelecimento/')({
  component: SelecionarEstabelecimentoPage,
  beforeLoad: () => {
    if (!authService.needsEstablishmentSelection()) {
      throw redirect({
        to: '/fila',
      });
    }
  },
});

const schema = z.object({
  establishmentId: z.string().min(1, 'Estabelecimento é obrigatório'),
});

type FormData = z.infer<typeof schema>;

function SelecionarEstabelecimentoPage() {
  const navigate = useNavigate();
  const methods = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const { mutate: selecionarEstabelecimento, isPending } = authQueries.useSelectEstablishment();

  // Get available establishments
  const establishments = authService.getEstablishments();

  useEffect(() => {
    // If there's only one establishment, select it automatically
    if (establishments.length === 1) {
      const singleEstablishment = establishments[0];
      console.log('Auto-selecionando o único estabelecimento disponível:', singleEstablishment.name);

      selecionarEstabelecimento(singleEstablishment.id, {
        onSuccess: () => {
          console.log('Estabelecimento selecionado automaticamente com sucesso no Fila');
          navigate({ to: '/fila' });
        },
        onError: (error) => {
          console.error('Erro ao auto-selecionar estabelecimento:', error);
        },
      });
    }
  }, [establishments, navigate, selecionarEstabelecimento]);

  // Show loading while auto-selecting or if there's only one establishment
  if (establishments.length <= 1) {
    return (
      <div className="fixed left-0 top-0 flex min-h-screen w-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-brand-primary-500" />
          <p className="text-lg font-medium text-gray-700">
            {establishments.length === 0
              ? 'Carregando estabelecimentos...'
              : 'Selecionando estabelecimento automaticamente...'
            }
          </p>
        </div>
      </div>
    );
  }

  function handleSelecionarEstabelecimento(data: z.infer<typeof schema>) {
    selecionarEstabelecimento(data.establishmentId, {
      onSuccess: () => {
        console.log('Estabelecimento selecionado com sucesso no Fila');
        navigate({ to: '/fila' });
      },
      onError: (error) => {
        console.error('Erro ao selecionar estabelecimento:', error);
      },
    });
  }

  return (
    <div className="fixed left-0 top-0 flex min-h-screen w-screen items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md rounded-lg p-0">
        <CardHeader className="mb-5 space-y-1 bg-brand-primary-500">
          <div className="mb-4 flex justify-center">
            <img src="/logo.png" alt="Logo" className="h-16 rounded-lg" />
          </div>
          <CardTitle className="text-center text-3xl font-bold text-white">Selecione seu estabelecimento</CardTitle>
          <CardDescription className="text-center text-white">Com qual estabelecimento deseja acessar o sistema?</CardDescription>
        </CardHeader>
        <CardContent>
          <Form ctx={methods} onSubmit={handleSelecionarEstabelecimento}>
            <Input
              type={EInput.SELECT}
              name="establishmentId"
              label="Estabelecimento"
              placeholder="Selecione seu estabelecimento"
              options={authService.getEstablishments().map((establishment) => ({ value: establishment.id, label: establishment.name }))}
            />

            <Button
              type="submit"
              variant="success"
              size="lg"
              className="mt-4 w-full bg-brand-primary-500 hover:bg-brand-primary-600"
              loading={isPending}>
              {isPending ? 'Aguarde...' : 'Selecionar estabelecimento'}
            </Button>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}